import React, {useEffect, useState} from "react";
import {Alert, Box, Button, Card, Grid, Stack, Tooltip, Typography} from "@mui/joy";
import IProfile from "../../types/profile.type";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import {Experimental_CssVarsProvider as CssVarsProvider} from "@mui/material/styles/ThemeProviderWithVars";
import {FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField} from "@mui/material";
import {GridSaveAltIcon} from "@mui/x-data-grid";
import {ErrorMessage, FieldArray, FormikProvider, useFormik} from "formik";
import moment, {Moment} from "moment/moment";
import * as Yup from "yup";
import { updateRelatives } from "../../services/profile.service";
import showNotification from "../utils/Notify";
import WorkIcon from "@mui/icons-material/Work";
import AddIcon from "@mui/icons-material/Add";
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import IconButton from "@mui/joy/IconButton";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import IRelative from "../../types/relative.type";
import CancelIcon from "@mui/icons-material/Cancel";
import BorderColorIcon from "@mui/icons-material/BorderColor";

interface Props {
  userId: any,
  profile: IProfile | undefined,
}

interface IRelativeData {
  relatives: Array<IRelative>
}

const RelativesDataForm: React.FC<Props> = (props) => {
  const { profile } = props;
  const [edit, setEdit] = useState(false);

  const initialValues: IRelativeData = {
    relatives: []
  }

  const validationSchema = Yup.object({
    relatives: Yup.array().min(1,"At least one cleaning expense is required").of(
      Yup.object().shape({
        relationship: Yup.string().required('El Parentesco / Relacion es requerido'),
        name: Yup.string().required('El Nombre es requerido'),
        lastName: Yup.string().required('El Apellido es requerido'),
        phone: Yup.string().required('El Telefono de Contacto es requerido'),
      })
    )
  });

  const onSubmit = (relativesData: IRelativeData) => {
    if (profile?.id) {
      updateRelatives(profile?.id, relativesData.relatives)
        .then((response) => {
          showNotification({type: 'success', message: 'Se actualizaron los datos Parentales y/o Familiares.'});
          profile.relatives = response.data.relatives;
          setEdit(false);
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    }
  };

  const formik = useFormik({
    enableReinitialize: false,
    initialValues: initialValues,
    onSubmit,
    validationSchema: validationSchema,
  });

  const handleOnChangeRelationship = (event: SelectChangeEvent, index: number) => {
    formik.values.relatives[index].relationship = event.target.value;
    formik.setFieldValue("relatives", formik.values.relatives);
  };

  const handleEditCancelClick = () => {
    formik.setTouched({});
    if (edit) {
      setEdit(false);
      formik.setValues({relatives: profile?.relatives ? profile?.relatives : []});
    } else {
      setEdit(true);
    }
  };

  useEffect(() => {
    formik.setValues({relatives: profile?.relatives ? profile?.relatives : []});
  }, [profile?.id]);

  return (
    <form className="form" onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <FieldArray
          name="relatives"
          render={(arrayHelpers) => (
            <Card sx={{gap: 0, marginTop: 2}}>
              <Stack direction={"row"} justifyContent={"space-between"} width="100%" >
                <Typography level="h4" startDecorator={<FamilyRestroomIcon/>}>Datos Parentales y/o Familiares</Typography>
                <Button
                  variant="soft"
                  onClick={() => arrayHelpers.push({relationship: null, name: null, lastName: null, phone: null})}
                  endDecorator={<AddIcon/>}
                  disabled={!edit}
                >
                  Agregar
                </Button>
              </Stack>
              {formik.values.relatives?.length == 0 && (
                <Alert variant="soft" sx={{marginTop: 1, justifyContent: 'center' }} >No hay datos Parentales y/o Familiares</Alert>
              )}
              {formik.values.relatives.map((relative, index) => (
                <Card sx={{gap: 0, marginTop:1, paddingTop: 0, paddingBottom: 0, paddingLeft: 2, paddingRight: 2}}>
                  <Stack direction="row" >
                    <Grid container key={`${index}`} rowSpacing={2} columnSpacing={2} marginBlock={1} sx={{width:'100%'}}>
                      <Grid xl={6} md={6} sm={6} xs={6}>
                        <CssVarsProvider>
                          <TextField
                            fullWidth
                            id={`relatives.${index}.name`}
                            name={`relatives.${index}.name`}
                            label="Nombre"
                            variant="filled"
                            type={"text"}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={relative.name}
                            slotProps={{inputLabel: {shrink: true}, input: {readOnly: !edit}}}
                          />
                          <ErrorMessage name={`relatives.${index}.name`}
                                        render={msg => <span className="Error-alert">{msg}</span>}/>
                        </CssVarsProvider>
                      </Grid>
                      <Grid xl={6} md={6} sm={6} xs={6}>
                        <CssVarsProvider>
                          <TextField
                            fullWidth
                            id={`relatives.${index}.lastName`}
                            name={`relatives.${index}.lastName`}
                            label="Apellido"
                            variant="filled"
                            type={"text"}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={relative.lastName}
                            slotProps={{inputLabel: {shrink: true}, input: {readOnly: !edit}}}
                          />
                          <ErrorMessage name={`relatives.${index}.lastName`}
                                        render={msg => <span className="Error-alert">{msg}</span>}/>
                        </CssVarsProvider>
                      </Grid>
                      <Grid xl={6} md={6} sm={6} xs={6}>
                        <CssVarsProvider>
                          <FormControl variant="filled" fullWidth>
                            <InputLabel id={`relatives.${index}.relationship`} shrink>Parentensco / Relacion</InputLabel>
                            <Select
                              label="Relationship"
                              labelId={`relatives.${index}.relationship`}
                              id={`relatives.${index}.relationship`}
                              name={`relatives.${index}.relationship`}
                              value={relative.relationship}
                              onBlur={formik.handleBlur}
                              slotProps={{input: {readOnly: !edit}}}
                              onChange={(event) => handleOnChangeRelationship(event, index)}
                            >
                              <MenuItem key="PARENT" value="PARENT">Padre/Madre</MenuItem>
                              <MenuItem key="CHILD" value="CHILD">Hijo/Hija</MenuItem>
                              <MenuItem key="SIBLING" value="SIBLING">Hermano/Hermana</MenuItem>
                              <MenuItem key="SPOUSE" value="SPOUSE">Esposo/Esposa</MenuItem>
                              <MenuItem key="PARTNER" value="PARTNER">Pareja</MenuItem>
                              <MenuItem key="RELATIVE" value="RELATIVE">Pariente</MenuItem>
                              <MenuItem key="OTHER" value="OTHER">Otro</MenuItem>
                            </Select>
                            <ErrorMessage name={`relatives.${index}.relationship`}
                                          render={msg => <span className="Error-alert">{msg}</span>}/>
                          </FormControl>
                        </CssVarsProvider>
                      </Grid>
                      <Grid xl={6} md={6} sm={6} xs={6}>
                        <CssVarsProvider>
                          <TextField
                            fullWidth
                            id={`relatives.${index}.phone`}
                            name={`relatives.${index}.phone`}
                            label="Telefono de Contacto"
                            variant="filled"
                            type={"text"}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={relative.phone}
                            slotProps={{inputLabel: {shrink: true}, input: {readOnly: !edit}}}
                          />
                          <ErrorMessage name={`relatives.${index}.phone`}
                                        render={msg => <span className="Error-alert">{msg}</span>}/>
                        </CssVarsProvider>
                      </Grid>
                    </Grid>
                    <IconButton
                      onClick={() => arrayHelpers.remove(index)}
                      style={{height: 60, marginLeft: 10, marginTop: 50}}
                      disabled={!edit}
                    >
                      <Tooltip title="Eliminar">
                        <DeleteForeverIcon sx={{fontSize: 40}}/>
                      </Tooltip>
                    </IconButton>
                  </Stack>
                </Card>
              ))}
              <Stack direction="row" justifyContent="flex-end" spacing={2} pt={1}>
                <Button variant="outlined" endDecorator={edit ? <CancelIcon/> : <BorderColorIcon />} onClick={handleEditCancelClick}>{edit ? 'Cancelar' : 'Editar'}</Button>
                <Button type="submit" variant="solid" endDecorator={<GridSaveAltIcon/>} disabled={!edit}>Guardar</Button>
              </Stack>
            </Card>
          )}
        />
      </FormikProvider>
    </form>
  );
};

export default RelativesDataForm;
